import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Collapse,
  Divider,
} from '@mui/material';
import {
  Dashboard,
  Work,
  Schedule,
  Assignment,
  School,
  AttachMoney,
  Person,
  Analytics,
  Build,
  Inventory,
  Groups,
  Folder,
  VerifiedUser,
  AccountBalance,
  Receipt,
  Assessment,
  ChangeCircle,
  PhotoCamera,
  BugReport,
  Web,
  Business,
  AdminPanelSettings,
  Subscriptions,
  SupportAgent,
  GpsFixed,
  CameraAlt,
  Sync,
  ExpandLess,
  ExpandMore,
  ShoppingCart,
  Store,
  RequestQuote,
  Category,
  Architecture,
  Draw,
  Edit,
  Approval,
  Timeline,
  SmartToy,
  Compare,
  AccountTree,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

interface NavItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  roles?: string[];
}

interface NavSection {
  title: string;
  items: NavItem[];
  roles?: string[];
}

const coreNavItems: NavItem[] = [
  {
    text: 'Dashboard',
    icon: <Dashboard />,
    path: '/dashboard',
  },
  {
    text: 'Analytics',
    icon: <Analytics />,
    path: '/analytics',
    roles: ['admin', 'manager'],
  },
  {
    text: 'Projects',
    icon: <Work />,
    path: '/projects',
  },
  {
    text: 'Profile',
    icon: <Person />,
    path: '/profile',
  },
];

const navSections: NavSection[] = [
  {
    title: 'Operations',
    items: [
      {
        text: 'Timesheets',
        icon: <Schedule />,
        path: '/timesheets',
      },
      {
        text: 'Daily Reports',
        icon: <Assignment />,
        path: '/daily-reports',
      },
      {
        text: 'Safety Reports',
        icon: <VerifiedUser />,
        path: '/safety-reports',
      },
      {
        text: 'Toolbox Talks',
        icon: <School />,
        path: '/toolbox-talks',
      },
      {
        text: 'Cost Codes',
        icon: <AttachMoney />,
        path: '/cost-codes',
        roles: ['admin', 'manager'],
      },
    ],
  },
  {
    title: 'Project & Resources',
    items: [
      {
        text: 'Equipment',
        icon: <Build />,
        path: '/equipment',
        roles: ['admin', 'manager', 'supervisor'],
      },
      {
        text: 'Materials',
        icon: <Inventory />,
        path: '/materials',
        roles: ['admin', 'manager', 'supervisor'],
      },
      {
        text: 'Subcontractors',
        icon: <Groups />,
        path: '/subcontractors',
        roles: ['admin', 'manager'],
      },
      {
        text: 'Documents',
        icon: <Folder />,
        path: '/documents',
      },
      {
        text: 'Quality Control',
        icon: <VerifiedUser />,
        path: '/quality-control',
        roles: ['admin', 'manager', 'supervisor'],
      },
    ],
    roles: ['admin', 'manager', 'supervisor'],
  },
  {
    title: 'Procurement',
    items: [
      {
        text: 'Procurement Dashboard',
        icon: <ShoppingCart />,
        path: '/procurement',
        roles: ['admin', 'manager', 'supervisor'],
      },
      {
        text: 'Vendors',
        icon: <Store />,
        path: '/vendors',
        roles: ['admin', 'manager', 'supervisor'],
      },
      {
        text: 'Purchase Orders',
        icon: <Receipt />,
        path: '/purchase-orders',
        roles: ['admin', 'manager', 'supervisor'],
      },
      {
        text: 'RFQs',
        icon: <RequestQuote />,
        path: '/rfqs',
        roles: ['admin', 'manager', 'supervisor'],
      },
      {
        text: 'Procurement Items',
        icon: <Category />,
        path: '/procurement-items',
        roles: ['admin', 'manager', 'supervisor'],
      },
    ],
    roles: ['admin', 'manager', 'supervisor'],
  },
  {
    title: 'Designs',
    items: [
      {
        text: 'Design Dashboard',
        icon: <Architecture />,
        path: '/designs',
        roles: ['admin', 'manager', 'supervisor'],
      },
      {
        text: 'Drawings',
        icon: <Draw />,
        path: '/drawings',
        roles: ['admin', 'manager', 'supervisor'],
      },
      {
        text: 'Design Revisions',
        icon: <Edit />,
        path: '/design-revisions',
        roles: ['admin', 'manager', 'supervisor'],
      },
      {
        text: 'Design Approvals',
        icon: <Approval />,
        path: '/design-approvals',
        roles: ['admin', 'manager', 'supervisor'],
      },
    ],
    roles: ['admin', 'manager', 'supervisor'],
  },
  {
    title: 'Project Planning',
    items: [
      {
        text: 'Planning Dashboard',
        icon: <Timeline />,
        path: '/project-planning',
        roles: ['admin', 'manager', 'supervisor'],
      },
    ],
    roles: ['admin', 'manager', 'supervisor'],
  },
  {
    title: 'AI Endpoints',
    items: [
      {
        text: 'Schedule Optimization',
        icon: <Timeline />,
        path: '/project-planning/ai/schedule-optimization',
        roles: ['admin', 'manager', 'supervisor'],
      },
      {
        text: 'Schedule Optimization History',
        icon: <Timeline />,
        path: '/project-planning/ai/schedule-optimization/history',
        roles: ['admin', 'manager', 'supervisor'],
      },
      {
        text: 'Delay Analysis',
        icon: <PhotoCamera />,
        path: '/project-planning/ai/delay-analysis',
        roles: ['admin', 'manager', 'supervisor'],
      },
      {
        text: 'Delay Analysis History',
        icon: <PhotoCamera />,
        path: '/project-planning/ai/delay-analysis/history',
        roles: ['admin', 'manager', 'supervisor'],
      },
      {
        text: 'Video Analyzer',
        icon: <CameraAlt />,
        path: '/project-planning/ai/video-analysis',
        roles: ['admin', 'manager', 'supervisor'],
      },
      {
        text: 'Video Analyzer History',
        icon: <CameraAlt />,
        path: '/project-planning/ai/video-analysis/history',
        roles: ['admin', 'manager', 'supervisor'],
      },
      {
        text: 'Overall Report',
        icon: <Assessment />,
        path: '/project-planning/ai/overall-report',
        roles: ['admin', 'manager', 'supervisor'],
      },
      {
        text: 'Overall Report History',
        icon: <Assessment />,
        path: '/project-planning/ai/overall-report/history',
        roles: ['admin', 'manager', 'supervisor'],
      },
      {
        text: 'Risk Forecast',
        icon: <Analytics />,
        path: '/project-planning/ai/risk-forecast',
        roles: ['admin', 'manager', 'supervisor'],
      },
      {
        text: 'Risk Forecast History',
        icon: <Analytics />,
        path: '/project-planning/ai/risk-forecast/history',
        roles: ['admin', 'manager', 'supervisor'],
      },
    ],
    roles: ['admin', 'manager', 'supervisor'],
  },
  {
    title: 'Financial',
    items: [
      {
        text: 'Budgets',
        icon: <AccountBalance />,
        path: '/budgets',
        roles: ['admin', 'manager'],
      },
      {
        text: 'Invoices',
        icon: <Receipt />,
        path: '/invoices',
        roles: ['admin', 'manager'],
      },
      {
        text: 'Financial Reports',
        icon: <Assessment />,
        path: '/financial-reports',
        roles: ['admin', 'manager'],
      },
      {
        text: 'Change Orders',
        icon: <ChangeCircle />,
        path: '/change-orders',
        roles: ['admin', 'manager'],
      },
    ],
    roles: ['admin', 'manager'],
  },
  {
    title: 'Communication',
    items: [
      {
        text: 'Photo Documentation',
        icon: <PhotoCamera />,
        path: '/photo-documentation',
      },
      {
        text: 'Issue Tracking',
        icon: <BugReport />,
        path: '/issue-tracking',
      },
      {
        text: 'Client Portal',
        icon: <Web />,
        path: '/client-portal',
        roles: ['admin', 'manager'],
      },
    ],
  },
  {
    title: 'Administration',
    items: [
      {
        text: 'Company Management',
        icon: <Business />,
        path: '/company-management',
        roles: ['admin'],
      },
      {
        text: 'User Management',
        icon: <Groups />,
        path: '/user-management',
        roles: ['admin'],
      },
      {
        text: 'Role Management',
        icon: <AdminPanelSettings />,
        path: '/role-management',
        roles: ['admin'],
      },
    ],
    roles: ['admin'],
  },
  {
    title: 'Business',
    items: [
      {
        text: 'Subscriptions',
        icon: <Subscriptions />,
        path: '/subscriptions',
        roles: ['admin'],
      },
    ],
    roles: ['admin', 'manager'],
  },
  {
    title: 'Mobile Management',
    items: [
      {
        text: 'Push Notifications',
        icon: <Web />,
        path: '/push-notifications',
        roles: ['admin', 'manager'],
      },
      {
        text: 'GPS Tracking',
        icon: <GpsFixed />,
        path: '/gps-tracking',
        roles: ['admin', 'manager', 'supervisor'],
      },
      {
        text: 'Camera Management',
        icon: <CameraAlt />,
        path: '/camera-management',
        roles: ['admin', 'manager'],
      },
      {
        text: 'Mobile Sync',
        icon: <Sync />,
        path: '/mobile-sync',
        roles: ['admin', 'manager'],
      },
    ],
    roles: ['admin', 'manager', 'supervisor'],
  },
];

const Sidebar: React.FC<SidebarProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [openSections, setOpenSections] = useState<{ [key: string]: boolean }>({});

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  const isItemVisible = (item: NavItem) => {
    if (!item.roles) return true;
    return user && item.roles.includes(user.role.toLowerCase());
  };

  const isSectionVisible = (section: NavSection) => {
    if (!section.roles) return true;
    return user && section.roles.some(role => role === user.role.toLowerCase());
  };

  const toggleSection = (sectionTitle: string) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionTitle]: !prev[sectionTitle]
    }));
  };

  const drawerWidth = 240;

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          p: 3,
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #dee2e6',
          textAlign: 'center',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c2c2c', fontSize: '1rem' }}>
          UpscaleSpacces
        </Typography>
        <Typography variant="caption" sx={{ color: '#6c757d', fontSize: '0.75rem' }}>
          UpscaleSpacces
        </Typography>
      </Box>
      
      <Box sx={{ flex: 1, py: 2 }}>
        <List sx={{ px: 1 }}>
          {coreNavItems
            .filter(isItemVisible)
            .map((item) => {
              const isActive = location.pathname === item.path;
              
              return (
                <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    selected={isActive}
                  >
                    <ListItemIcon
                      sx={{
                        color: isActive ? '#ffffff' : '#6c757d',
                        minWidth: 36,
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.text}
                      sx={{
                        '& .MuiListItemText-primary': {
                          color: isActive ? '#ffffff' : '#2c2c2c',
                        }
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
        </List>
        
        <Divider sx={{ mx: 2, my: 1 }} />
        
        {navSections
          .filter(isSectionVisible)
          .map((section) => {
            const filteredItems = section.items.filter(isItemVisible);
            if (filteredItems.length === 0) return null;
            
            const isOpen = openSections[section.title] ?? false;
            
            return (
              <React.Fragment key={section.title}>
                <List sx={{ px: 1 }}>
                  <ListItem disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      onClick={() => toggleSection(section.title)}
                      sx={{
                        '&:hover': {
                          backgroundColor: '#f8f9fa',
                        },
                      }}
                    >
                      <ListItemText 
                        primary={section.title}
                        primaryTypographyProps={{
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: '#6c757d',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}
                      />
                      {isOpen ? <ExpandLess sx={{ color: '#6c757d' }} /> : <ExpandMore sx={{ color: '#6c757d' }} />}
                    </ListItemButton>
                  </ListItem>
                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    {filteredItems.map((item) => {
                      const isActive = location.pathname === item.path;
                      
                      return (
                        <ListItem key={item.text} disablePadding sx={{ mb: 0.5, pl: 2 }}>
                          <ListItemButton
                            onClick={() => handleNavigation(item.path)}
                            selected={isActive}
                          >
                            <ListItemIcon
                              sx={{
                                color: isActive ? '#ffffff' : '#6c757d',
                                minWidth: 36,
                              }}
                            >
                              {item.icon}
                            </ListItemIcon>
                            <ListItemText 
                              primary={item.text}
                              sx={{
                                '& .MuiListItemText-primary': {
                                  color: isActive ? '#ffffff' : '#2c2c2c',
                                }
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </Collapse>
                </List>
              </React.Fragment>
            );
          })}
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
    >
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
          },
        }}
      >
        {drawer}
      </Drawer>
      
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            position: 'fixed',
            height: '100vh',
            top: 0,
            left: 0,
            zIndex: 1200,
          },
        }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;